import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Rnd } from 'react-rnd';

import { Box, Button, Paper, Portal, Typography } from '@mui/material';
import { GripIcon } from '@sharedCR/coreDesignSystem/icons';
import DynamicFieldItemsList from 'app/shared/components/forms/FormEditor/dynamicField/DynamicFieldItemsList';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { DefaultTemplateElementPosition, FieldType } from 'constants/forms';
import { isPdfFormType } from 'utils/forms';

import { createTemplateElementAction } from 'store/forms/actions';
import {
  selectDynamicFieldsMap,
  selectDynamicFieldsSchema,
  selectFormType,
  selectPdfFormPageExtendedElements,
} from 'store/forms/selectors';

import { FormsTestIds } from '../../Forms.testIds';

import {
  BATCH_PANEL_CLOSE_KEY,
  BATCH_PANEL_DONE_LABEL,
  BATCH_PANEL_DRAG_HANDLE_CLASS,
  BATCH_PANEL_EXCLUDED_HINT,
  BATCH_PANEL_HINT,
  BATCH_PANEL_TITLE,
} from './BatchAddDynamicFields.constants';
import {
  batchPanelFooterStyles,
  batchPanelHeaderStyles,
  batchPanelLayerStyles,
  batchPanelPaperStyles,
  batchPanelRndStyle,
  batchPanelTitleRowStyles,
} from './BatchAddDynamicFields.styles';
import {
  getBatchableDynamicFieldsSchema,
  getBatchDynamicField,
  getBatchElementPosition,
  getInitialPanelPosition,
} from './BatchAddDynamicFields.utils';

const BatchAddDynamicFields = ({ selectedPage, onClose, anchorEl = null }) => {
  const dispatch = useDispatch();

  const dynamicFieldsSchema = useSelector(selectDynamicFieldsSchema);
  const dynamicFieldsMap = useSelector(selectDynamicFieldsMap);
  const formType = useSelector(selectFormType);
  const pageElements = useSelector(selectPdfFormPageExtendedElements(selectedPage, 1));

  const [addedValues, setAddedValues] = useState([]);
  const [draggedPosition, setDraggedPosition] = useState(null);

  // Clicks faster than a re-render would otherwise read the same page elements and pick the same slot.
  const placedElementsRef = useRef(pageElements);

  const titleId = useId();

  const isPdfForm = isPdfFormType(formType);

  // The list copies the schema into local state on mount, so it must remount whenever a new schema loads.
  const { fieldListKey, dynamicFieldConfig } = useMemo(
    () => ({
      fieldListKey: _.uniqueId('dynamic-field-schema-'),
      dynamicFieldConfig: {
        dynamicFieldsSchema: getBatchableDynamicFieldsSchema(dynamicFieldsSchema ?? []),
      },
    }),
    [dynamicFieldsSchema],
  );
  const chosenOption = useMemo(() => ['', _.last(addedValues) ?? ''], [addedValues]);
  const panelPosition = useMemo(() => {
    if (draggedPosition || !anchorEl) {
      return draggedPosition;
    }

    return getInitialPanelPosition({
      anchorRect: anchorEl.getBoundingClientRect(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  }, [draggedPosition, anchorEl]);

  const onSelectField = useCallback(
    ({ value }) => {
      const field = dynamicFieldsMap[value];

      if (!field) {
        return;
      }

      const position = getBatchElementPosition({
        type: field.type,
        pageElements: placedElementsRef.current,
      });

      placedElementsRef.current = [
        ...placedElementsRef.current,
        { position: { ...DefaultTemplateElementPosition[FieldType.DYNAMIC], ...position } },
      ];

      dispatch(
        createTemplateElementAction({
          selectedPage,
          fieldType: FieldType.DYNAMIC,
          field: getBatchDynamicField({ field, isPdfForm }),
          position,
        }),
      );

      setAddedValues(prev => [...prev, value]);
    },
    [dynamicFieldsMap, selectedPage, dispatch, isPdfForm],
  );

  const onDone = useCallback(() => {
    setAddedValues([]);
    onClose();
  }, [onClose]);

  const onDragStop = useCallback((__, { x, y }) => setDraggedPosition({ x, y }), []);

  useEffect(() => {
    placedElementsRef.current = pageElements;
  }, [pageElements]);

  useEffect(() => {
    if (!anchorEl) {
      return;
    }

    const onDocumentKeyDown = e => {
      if (e.key === BATCH_PANEL_CLOSE_KEY) {
        onDone();
      }
    };

    document.addEventListener('keydown', onDocumentKeyDown);

    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [anchorEl, onDone]);

  if (!anchorEl) {
    return null;
  }

  return (
    <Portal>
      <Box sx={batchPanelLayerStyles}>
        <Rnd
          bounds="parent"
          dragHandleClassName={BATCH_PANEL_DRAG_HANDLE_CLASS}
          enableResizing={false}
          position={panelPosition}
          style={batchPanelRndStyle}
          onDragStop={onDragStop}
        >
          <Paper
            aria-labelledby={titleId}
            data-testid={FormsTestIds.PDF_EDITOR_BATCH_ADD_PANEL}
            elevation={8}
            role="dialog"
            sx={batchPanelPaperStyles}
          >
            <Box sx={batchPanelHeaderStyles}>
              <Box
                className={BATCH_PANEL_DRAG_HANDLE_CLASS}
                data-testid={FormsTestIds.PDF_EDITOR_BATCH_ADD_DRAG_HANDLE}
                sx={batchPanelTitleRowStyles}
              >
                <GripIcon size={IconSize[16]} />
                <Typography color="text.primary" id={titleId} variant="medium16">
                  {BATCH_PANEL_TITLE}
                </Typography>
              </Box>
              <Typography color="text.secondary" variant="regular12">
                {BATCH_PANEL_HINT}
              </Typography>
              <Typography color="text.secondary" variant="regular12">
                {BATCH_PANEL_EXCLUDED_HINT}
              </Typography>
            </Box>

            <DynamicFieldItemsList
              addedValues={addedValues}
              chosenOption={chosenOption}
              dynamicFieldConfig={dynamicFieldConfig}
              key={fieldListKey}
              setChosenOption={_.noop}
              onSelectField={onSelectField}
            />

            <Box sx={batchPanelFooterStyles}>
              <Typography
                color="text.secondary"
                data-testid={FormsTestIds.PDF_EDITOR_BATCH_ADD_COUNT}
                variant="regular14"
              >
                {`${addedValues.length} added`}
              </Typography>

              <Button
                color="primary"
                data-testid={FormsTestIds.PDF_EDITOR_BATCH_ADD_DONE_BUTTON}
                size="small"
                variant="contained"
                onClick={onDone}
              >
                {BATCH_PANEL_DONE_LABEL}
              </Button>
            </Box>
          </Paper>
        </Rnd>
      </Box>
    </Portal>
  );
};

BatchAddDynamicFields.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
};

export default BatchAddDynamicFields;
